// In-memory session token (VAPT CWE-922).
//
// The JWT is no longer written to localStorage. The server sets it as an
// HttpOnly, Secure, SameSite cookie at login / refresh, and every request is
// sent with credentials so the cookie authenticates it. A copy is kept here,
// in memory only, for the few places that must put the token in a URL or
// header (websocket handshake, image links, the app webview hand-off) and
// for the client-side expiry check that drives /refresh.
//
// After a page reload the memory copy is gone; ensureToken() asks /refresh
// (cookie-authenticated) for it once before the app renders.
import { serverConfig } from 'src/settings';

let token = '';
let exp = 0; // ms since epoch, 0 = unknown
let bootstrap = null;

// Local dev only: the HttpOnly cookie belongs to the API host (SameSite=Lax),
// so a page on localhost never gets it back and every reload / hot reload
// signed you out. There the token is also kept in localStorage (shared by
// all localhost tabs). Production builds never take this path.
const DEV_KEY = 'devToken';
const devStore = process.env.NODE_ENV === 'development'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname);
const devSave = (t) => {
  if (!devStore) return;
  try {
    if (t) window.localStorage.setItem(DEV_KEY, t); else window.localStorage.removeItem(DEV_KEY);
  } catch (_) { /* storage blocked */ }
};
const devLoad = () => {
  if (!devStore) return '';
  try { return window.localStorage.getItem(DEV_KEY) || ''; } catch (_) { return ''; }
};

const decodeExp = (t) => {
  try {
    const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return (payload?.exp || 0) * 1000;
  } catch (_) {
    return 0;
  }
};

export const setToken = (t) => {
  token = t || '';
  exp = token ? decodeExp(token) : 0;
  devSave(token);
};

export const getToken = () => token;

export const getTokenExp = () => exp;

export const hasToken = () => !!token;

export const clearToken = () => {
  token = '';
  exp = 0;
  devSave('');
};

// Drops the copies older builds persisted, so a stale token never lingers
// in the browser after this build is deployed.
export const purgeLegacyStorage = () => {
  try {
    window.localStorage.removeItem('token');
  } catch (_) { /* storage blocked */ }
};

// Resolves with the token (or '' when there is no live session). Shared
// across callers so a reload issues a single /refresh.
export const ensureToken = () => {
  if (token) return Promise.resolve(token);
  const saved = devLoad();
  if (saved && decodeExp(saved) > Date.now()) {
    setToken(saved);
    return Promise.resolve(token);
  }
  if (!bootstrap) {
    bootstrap = fetch(`${serverConfig.baseUrl}refresh`, { method: 'GET', credentials: 'include' })
      .then((r) => r.json())
      .then((j) => {
        if (j && j.result === 'OK' && j.token) setToken(j.token);
        return token;
      })
      .catch(() => token)
      .finally(() => { bootstrap = null; });
  }
  return bootstrap;
};
